import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../context/LanguageContext";
import { DataTable } from "../../../../components/DataTable";
import { toast } from "sonner";
import { BaseUrlApi, ErrorMessage } from "../../../../lib/api";
import axios from "axios";
import { Button } from "../../../../components/ui/button";
import { ArrowUpDown } from "lucide-react";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { Select, Spin } from "antd";
import { useAuth } from "../../../../context/AuthContext";
import { GradebookData } from "../../../../lib/languages";
import { parseField } from "../../../../lib/utils";

export default function GradebookPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = GradebookData[language] || GradebookData["en"];

  const [activeTab, setActiveTab] = useState("Quiz");
  const [gradebook, setGradebook] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [apiUrl, setApiUrl] = useState(null);

  // Set ApiUrl based on user's role whenever user changes.
  useEffect(() => {
    if (user) {
      const url =
        user?.role === "admin"
          ? "admin/students/guidebook"
          : "teacher/students/guidebook";
      setApiUrl(url);
    }
  }, [user]);

  // Fetch gradebook from the API.
  const fetchGradebook = async () => {
    if (!apiUrl) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`${BaseUrlApi}/${apiUrl}`);
      if (data?.data && Array.isArray(data.data)) {
        setGradebook(data.data);
        // Initialize filteredData if data is available
        if (data.data.length > 0) {
          const defaultData =
            activeTab === "Quiz"
              ? data.data[0].quiz_attempts || []
              : data.data[0].answer_assignments || [];
          setFilteredData(defaultData);
        }
      } else {
        toast("Error", { description: "Unexpected data format." });
      }
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") {
        toast("Error", { description: result });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (apiUrl) {
      fetchGradebook();
    }
  }, [apiUrl]);

  // Define the table columns.
  const quizColumn = [
    {
      accessorKey: language === "en" ? "quiz.title.en" : "quiz.title.ar",
      header: t.quizName,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <span className="flex items-center gap-1">
            {parseField(item?.quiz?.title, language)}
          </span>
        );
      },
    },
    {
      accessorKey: "score",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {t.score} <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <span className="flex items-center gap-1">
            {textColor(item.score, item.total_quiz_score)}
            <span>/</span>
            {item.total_quiz_score || "---"}
          </span>
        );
      },
    },
  ];

  const assignmentColumn = [
    {
      accessorKey:
        language === "en" ? "assignment.title.en" : "assignment.title.ar",
      header: t.assignmentName,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <span className="flex items-center gap-1">
            {parseField(item?.assignment?.title, language)}
          </span>
        );
      },
    },
    {
      accessorKey: "score",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {t.score} <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <span className="flex items-center gap-1">
            {textColor(item?.score, item?.assignment?.score)}
            <span>/</span>
            {item?.assignment?.score || "---"}
          </span>
        );
      },
    },
  ];

  const textColor = (score, maxScore) => {
    if (score === maxScore) {
      return <p className="text-green-500 font-semibold">{score}</p>;
    } else if (maxScore && score >= maxScore * 0.5) {
      return <p className="text-yellow-500 font-semibold">{score}</p>;
    } else {
      return <p className="text-red-500 font-semibold">{score}</p>;
    }
  };

  // Generate options for student selection.
  const studentOptions = gradebook
    .filter((item) => item.role === "student")
    .map((student) => ({
      value: student.id,
      label: student.name,
    }));

  // Update filteredData when a student is selected.
  const handleStudentChange = (selectedStudentId) => {
    const selectedStudent = gradebook.find(
      (item) => item.id === selectedStudentId
    );

    if (!selectedStudent) {
      setFilteredData([]);
      return;
    }

    const newFilteredData =
      activeTab === "Quiz"
        ? selectedStudent.quiz_attempts || []
        : selectedStudent.answer_assignments || [];

    setFilteredData(newFilteredData);
  };

  // Update filteredData when activeTab or gradebook changes.
  useEffect(() => {
    if (gradebook.length > 0) {
      const newData =
        activeTab === "Quiz"
          ? gradebook[0].quiz_attempts || []
          : gradebook[0].answer_assignments || [];
      setFilteredData(newData);
    } else {
      setFilteredData([]);
    }
  }, [activeTab, gradebook]);

  return (
    <div className="space-y-8" dir={language === "ar" ? "rtl" : "ltr"}>
      <h1 className="text-2xl font-bold">{t.gradebook}</h1>
      <Tabs
        defaultValue={activeTab}
        className="w-full"
        onValueChange={setActiveTab}
      >
        {!loading ? (
          <>
            <div
              className="flex items-center flex-wrap sm:flex-nowrap gap-2"
              dir={language === "ar" ? "rtl" : "ltr"}
            >
              <TabsList className="w-full border">
                <TabsTrigger value="Quiz" className="flex-1">
                  {t.quiz}
                </TabsTrigger>
                <TabsTrigger value="Assignment" className="flex-1">
                  {t.assignment}
                </TabsTrigger>
              </TabsList>
              <Select
                showSearch
                className="w-full sm:w-1/4"
                placeholder={t.searchStudent}
                optionFilterProp="label"
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
                defaultValue={studentOptions[0]}
                options={studentOptions}
                onChange={handleStudentChange}
              />
            </div>
            <TabsContent value="Quiz" dir={language === "ar" ? "rtl" : "ltr"}>
              <DataTable
                columns={quizColumn}
                data={filteredData}
                title={t.quizDataTitle}
                filterNames={["quiz_title_en", "quiz_title_ar", "score"]}
              />
            </TabsContent>
            <TabsContent
              value="Assignment"
              dir={language === "ar" ? "rtl" : "ltr"}
            >
              <DataTable
                columns={assignmentColumn}
                data={filteredData}
                title={t.assignmentDataTitle}
                filterNames={[
                  "assignment_title_en",
                  "assignment_title_ar",
                  "score",
                ]}
              />
            </TabsContent>
          </>
        ) : (
          <div className="h-40 w-full flex items-center justify-center">
            <Spin size="large" />
          </div>
        )}
      </Tabs>
    </div>
  );
}
